// ===================================================
// guestStorage.js — 비로그인(게스트) 상태의 데이터 저장 유틸
//
// 역할:
//   - 로그인 없이도 기록을 브라우저 로컬스토리지에 저장
//   - storage.js(Supabase)와 동일한 데이터 형식을 사용해
//     로그인 시 DB로 매끄럽게 마이그레이션 가능
//   - 네이티브 앱에서는 appStorage를 통해 Preferences에도 이중 저장
//     → iOS가 WebView 저장소를 초기화해도 데이터 보존
//
// 저장 형식:
//   저장 키: 'omsal_guest_sessions'
//   값: 세션 객체 배열 (JSON)
// ===================================================

import { appRead, appWrite, appRemove } from './appStorage';
import { getToday } from './dateUtils';

const GUEST_SESSIONS_KEY = 'omsal_guest_sessions';

// 게스트 세션용 고유 ID 생성
function generateId() {
  return `guest_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

// 저장소에서 세션 배열 읽기
function getRaw() {
  try {
    return JSON.parse(appRead(GUEST_SESSIONS_KEY) || '[]');
  } catch {
    return [];
  }
}

// 저장소에 세션 배열 쓰기
function setRaw(arr) {
  appWrite(GUEST_SESSIONS_KEY, JSON.stringify(arr));
}

// ── 세션 기록 ───────────────────────────────────────────────

// 저장된 게스트 기록 불러오기
// 반환 형식: { "YYYY-MM-DD": [session, ...], ... } (loadSessions와 동일)
export function loadGuestSessions() {
  const result = {};
  for (const s of getRaw()) {
    if (!result[s.date]) result[s.date] = [];
    result[s.date].push(s);
  }
  return result;
}

// 새 게스트 세션 저장
export function saveGuestSession(session) {
  const list = getRaw();
  list.push({
    id: generateId(),
    date: getToday(),
    mode: session.mode,
    initialText: session.initialText,
    rounds: session.rounds || [],
    keywords: session.keywords || [],
    title: session.title || null,
    contextSummary: session.contextSummary || null,
    // 의사결정 모드일 때만 저장
    decisionMeta: session.decisionMeta || null,
    pinned: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  setRaw(list);
}

// 특정 게스트 세션 수정
export function updateGuestSession(id, fields) {
  const list = getRaw();
  const idx = list.findIndex(s => s.id === id);
  if (idx === -1) return;
  list[idx] = {
    ...list[idx],
    ...(fields.initialText !== undefined  && { initialText: fields.initialText }),
    ...(fields.rounds !== undefined       && { rounds: fields.rounds }),
    ...(fields.keywords !== undefined     && { keywords: fields.keywords }),
    ...(fields.contextSummary !== undefined && { contextSummary: fields.contextSummary }),
    ...(fields.title !== undefined        && { title: fields.title }),
    ...(fields.decisionMeta !== undefined && { decisionMeta: fields.decisionMeta }),
    ...(fields.pinned !== undefined && { pinned: fields.pinned }),
    updatedAt: new Date().toISOString(),
  };
  setRaw(list);
}

// 특정 게스트 세션 삭제
export function deleteGuestSession(id) {
  setRaw(getRaw().filter(s => s.id !== id));
}

// 게스트 세션 전체 삭제 (로그인 권유 배너 표시 여부 포함)
export function deleteAllGuestSessions() {
  appRemove(GUEST_SESSIONS_KEY);
}

// 현재 저장된 게스트 세션 총 개수 (로그인 권유 배너 표시 여부 판단용)
export function getGuestSessionCount() {
  return getRaw().length;
}

// DB 마이그레이션용: 세션 목록을 날짜 포함 평면 배열로 반환
export function getAllGuestSessionsFlat() {
  return getRaw();
}
